import { useState } from "react";
import { createContainer } from "unstated-next";
import { CampaignService } from "../services/CampaignService";
import { Timestamp } from "../utilities/Firebase";
import { LineElement } from "../components/elements/text-decoration/TextDecoration";

type Campaign = {
    status: "active" | "preparing" | "end";
    outline: {
        period: {
            from: Date;
            to: Date;
        };
        incentive: {
            distributionDate?: Date;
            expirationDate?: Date;
        };
        targetShops: string;
        targetShopsLink: {
            label: string,
            url: string
        };
        title: string;
    };
    assets: {
        mainBanner: string;
        landingPageLink: string;
    };
    messages: {
        description: LineElement[];
        complete: LineElement[];
    };
    notices: {
        overall: LineElement[];
        incentive: LineElement[];
        shopDescription: string[];
        finishedDescription: string[];
        pointCard: LineElement[];
    };
    setting: {
        targetAmount: number;
        incentive: {
            point: number;
            maxCount: number;
        };
        floors: string[];
        defaultRecommends: string[];
    };
};

/**
 * キャンペーンコンテナ
 */
const useCampaignContainer = () => {
    // URL の先頭からキャンペーンIDを取得
    const path = window.location.pathname;
    /** キャンペーンID */
    const campaignId = path.slice(1).split("/")[0];
    /** キャンペーンデータ */
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    /** 設定人数到達状況 */
    const [isReached, setIsReached] = useState<boolean | undefined>(undefined);

    /**
     * キャンペーンデータ初期化
     * @param id キャンペーンID
     */
    const initCampaign = async (id: string) => {
        const campaignData = await CampaignService().getCampaign(id);

        if (campaignData) {
            const campaign = {
                status: getCampaignStatus(campaignData.outline.period),
                outline: {
                    period: {
                        from: campaignData.outline.period.from.toDate(),
                        to: campaignData.outline.period.to.toDate(),
                    },
                    incentive: {
                        distributionDate:
                            campaignData.outline.incentive.distributionDate ? campaignData.outline.incentive.distributionDate.toDate() : undefined,
                        expirationDate:
                            campaignData.outline.incentive.expirationDate ? campaignData.outline.incentive.expirationDate.toDate() : undefined,
                    },
                    targetShops: campaignData.outline.targetShops,
                    targetShopsLink: campaignData.outline.targetShopsLink,
                    title: campaignData.outline.title,
                },
                assets: campaignData.assets,
                messages: campaignData.messages,
                notices: campaignData.notices,
                setting: campaignData.setting,
            };
            setCampaign({ ...campaign });
        }
    };

    /**
     * キャンペーン期間と現在時刻を比較し、実施ステータスを取得
     * @param period キャンペーン期間
     * @returns ステータス
     */
    const getCampaignStatus = (period: any): "active" | "preparing" | "end" => {
        const current = Timestamp.now();

        const before = period.from > current;
        if (before) {
            return "preparing";
        }

        const after = period.to < current;
        if (after) {
            return "end";
        }

        return "active";
    };

    /**
     * キャンペーン達成数を確認
     * @param id キャンペーンID
     */
    const checkAchievedParticipants = async (id: string) => {
        if (!campaign) {
            return;
        }

        const achieved = await CampaignService().getAchievedParticipants(id);
        if (achieved) {
            const achievedCount = achieved.count;
            const maxCount = campaign.setting.incentive.maxCount;

            setIsReached(achievedCount >= maxCount);
        }
    };

    return {
        campaignId,
        campaign,
        isReached,
        initCampaign,
        checkAchievedParticipants,
    };
};

export const CampaignContainer = createContainer(useCampaignContainer);
